import { GameState, League, Player, CupFixture, Morale, DisplayFixture, Result, MatchEvent, Fixture, Cup } from '../../domain/entities';
import { getPlayerTeam, addNewsItem, findPlayerInState } from '../GameService';
import { simulateMatch, generateDetailedEvents, simulatePenaltyShootout } from '../MatchSimulator';
import { updateAILineup, updateAITactics, updateAITrainingFocus } from '../TeamService';
import { simulateAIInSeasonTransfers, updateAITransferLists } from '../AITransferService';
import { refreshYouthSquad } from '../YouthDevelopmentService';
import { CupService } from '../CupService';
import { FixturesHelper } from '../FixturesHelper';
import { AiNewsService } from '../AiNewsService';
import { getPlayerValue, TRAINING_FACILITIES_LEVELS, YOUTH_ACADEMY_LEVELS, calculateExpectedWage, ASSISTANT_MANAGER_LEVELS, isPlayerInTransferCooldown, calculateReleasePenalty } from '../../domain/constants';
import { AiService } from '../AiService';
import { useAssistantManagerManagement } from '../../application/hooks/management/useAssistantManagerManagement';
import { AssistantManagerBriefingService } from '../AssistantManagerBriefingService';
import { OfflineAiService } from '../OfflineAiService';
import { AIFacilityManagementService } from '../AIFacilityManagementService';
import { AIBiddingService } from '../AIBiddingService';
import { FreeAgentService } from '../FreeAgentService';
// Processors
import { InjuryProcessor } from './processors/InjuryProcessor';
import { FormProcessor } from './processors/FormProcessor';
import { MoraleProcessor } from './processors/MoraleProcessor';
import { FitnessProcessor } from './processors/FitnessProcessor';
import { TrainingProcessor } from './processors/TrainingProcessor';

const updateLeagueTable = (team: any, goalsFor: number, goalsAgainst: number) => {
    team.played++;
    team.goalsFor += goalsFor;
    team.goalsAgainst += goalsAgainst;
    if (goalsFor > goalsAgainst) {
        team.won++;
        team.points += 3;
    } else if (goalsFor === goalsAgainst) {
        team.drawn++;
        team.points += 1;
    } else {
        team.lost++;
    }
};

const sortLeague = (league: League) => {
    league.teams.sort((a, b) => {
        if (a.points !== b.points) return b.points - a.points;
        const aGD = a.goalsFor - a.goalsAgainst;
        const bGD = b.goalsFor - b.goalsAgainst;
        if (aGD !== bGD) return bGD - aGD;
        if (a.goalsFor !== b.goalsFor) return b.goalsFor - b.goalsFor;
        return a.name.localeCompare(b.name);
    });
};

const updateClubHistoryStats = (team: any, playersWhoPlayed: Set<number>, matchScorers: number[]) => {
    playersWhoPlayed.forEach(playerId => {
        if (team.squad.some((p: any) => p.id === playerId)) {
            if (!team.clubHistory.playerStats[playerId]) {
                const player = team.squad.find((p: any) => p.id === playerId);
                if(player) {
                    team.clubHistory.playerStats[playerId] = { id: playerId, name: player.name, appearances: 0, goals: 0 };
                }
            }
            if (team.clubHistory.playerStats[playerId]) {
                team.clubHistory.playerStats[playerId].appearances++;
            }
        }
    });

    matchScorers.forEach(playerId => {
         if (team.squad.some((p: any) => p.id === playerId)) {
            if (team.clubHistory.playerStats[playerId]) {
                 team.clubHistory.playerStats[playerId].goals++;
            }
         }
    });
}

const processMatchStats = (result: Result, allPlayersById: Map<number, Player>) => {
    // Increment games played for all players who participated
    const allPlayersInMatch = new Set<number>([
        ...result.homeStartingXI,
        ...result.awayStartingXI,
    ]);
    allPlayersInMatch.forEach(playerId => {
        const p = allPlayersById.get(playerId);
        if (p) p.gamesPlayed = (p.gamesPlayed || 0) + 1;
    });

    result.homeScorers.forEach(id => { const p = allPlayersById.get(id); if (p) p.goalsScored = (p.goalsScored || 0) + 1; });
    result.awayScorers.forEach(id => { const p = allPlayersById.get(id); if (p) p.goalsScored = (p.goalsScored || 0) + 1; });
    result.homeAssists.forEach(({ assisterId }) => { const p = allPlayersById.get(assisterId); if (p) p.assists = (p.assists || 0) + 1; });
    result.awayAssists.forEach(({ assisterId }) => { const p = allPlayersById.get(assisterId); if (p) p.assists = (p.assists || 0) + 1; });

    // Track yellow cards and suspensions
    result.yellowCards.forEach(({ playerId }) => {
        const p = allPlayersById.get(playerId);
        if (p) {
            p.yellowCards = (p.yellowCards || 0) + 1;
            p.yellowCardAccumulation = (p.yellowCardAccumulation || 0) + 1;

            // Check if player has received 2 yellow cards in this match (second yellow = red card)
            const yellowsThisMatch = result.yellowCards.filter(c => c.playerId === playerId).length;
            const gotRedThisMatch = result.redCards.some(c => c.playerId === playerId);
            // If they got a red from 2 yellows, apply 1-match suspension (or extend existing)
            if (yellowsThisMatch >= 2 || gotRedThisMatch) {
                if (!p.suspension || p.suspension.matchesRemaining < 1) {
                    p.suspension = { matchesRemaining: 1, reason: 'Yellow Cards' };
                } else {
                    // Extend existing suspension if this is more severe
                    p.suspension.matchesRemaining = Math.max(p.suspension.matchesRemaining, 1);
                }
            }
            // Check for yellow card accumulation (5 yellows = 1 match ban, 10 yellows = 2 match ban)
            else if (p.yellowCardAccumulation === 5) {
                if (!p.suspension || p.suspension.matchesRemaining < 1) {
                    p.suspension = { matchesRemaining: 1, reason: 'Yellow Card Accumulation' };
                }
            } else if (p.yellowCardAccumulation === 10) {
                if (!p.suspension || p.suspension.matchesRemaining < 2) {
                    p.suspension = { matchesRemaining: 2, reason: 'Yellow Card Accumulation' };
                }
            } else if (p.yellowCardAccumulation === 15) {
                if (!p.suspension || p.suspension.matchesRemaining < 3) {
                    p.suspension = { matchesRemaining: 3, reason: 'Yellow Card Accumulation' };
                }
            }
        }
    });

    // Track red cards and suspensions (3-match ban for straight red)
    result.redCards.forEach(({ playerId }) => {
        const p = allPlayersById.get(playerId);
        if (p) {
            p.redCards = (p.redCards || 0) + 1;
            // Check if this was a straight red (not from 2 yellows)
            const yellowsThisMatch = result.yellowCards.filter(c => c.playerId === playerId).length;
            if (yellowsThisMatch < 2) {
                // Straight red = 3 match ban
                p.suspension = { matchesRemaining: 3, reason: 'Red Card' };
            }
            // Note: if it was 2 yellows, the suspension was already set above
        }
    });

    if (result.potm) { const p = allPlayersById.get(result.potm.playerId); if (p) p.potmAwards = (p.potmAwards || 0) + 1; }
};

// Process suspensions - reduce matches remaining for suspended players, clear if done
const processSuspensions = (team: any) => {
    team.squad.forEach((player: Player) => {
        if (player.suspension && player.suspension.matchesRemaining > 0) {
            player.suspension.matchesRemaining--;
            if (player.suspension.matchesRemaining === 0) {
                player.suspension = null;
            }
        }
    });
};

// Morale maps for bench player processing (starter morale handled by MoraleProcessor)
const moraleMap: Record<Morale, number> = { 'Ecstatic': 5, 'Happy': 4, 'Content': 3, 'Unhappy': 2, 'Furious': 1 };
const numberToMorale: Record<number, Morale> = { 5: 'Ecstatic', 4: 'Happy', 3: 'Content', 2: 'Unhappy', 1: 'Furious' };

const changeMorale = (currentMorale: Morale, change: number): Morale => {
    const currentLevel = moraleMap[currentMorale];
    const newLevel = Math.max(1, Math.min(5, currentLevel + change));
    return numberToMorale[newLevel];
};

// Process morale for bench players (lack of game time) - called after each match
const processBenchPlayerMorale = (team: any, startingXI: number[], gameState: GameState) => {
    team.squad.forEach((player: Player) => {
        if (!startingXI.includes(player.id) && !player.injury) {
            const oldMorale = player.morale;
            let moraleChange = 0;
            // Skilled players get frustrated on bench
            if (player.skill > team.baseSkill && Math.random() < 0.05) moraleChange--;  // 5% chance
            // Unhappy players feel better when settled
            else if (moraleMap[player.morale] < 3 && Math.random() < 0.1) moraleChange++;

            if (moraleChange !== 0) {
                player.morale = changeMorale(player.morale, moraleChange);
                player.moraleReason = moraleChange < 0 ? 'Lack of game time' : 'Feeling more settled';
            }

            if (player.morale !== oldMorale && team.name === gameState.playerTeamName) {
                const oldLevel = moraleMap[oldMorale];
                const newLevel = moraleMap[player.morale];
                if (newLevel < oldLevel && newLevel <= 2 && oldLevel > 2) {
                    player.wantsToTalk = true;
                    addNewsItem(gameState, 'Player Wants to Talk', `${player.name} is unhappy due to '${player.moraleReason}'. They want to talk. Their morale is now '${player.morale}'.`, 'morale', undefined, { type: 'team', title: 'My Team', activeTab: 'morale' });
                }
            }
        }
    });
};

// Training and fitness processing moved to processors/TrainingProcessor.ts and processors/FitnessProcessor.ts

// Process listed players and find buyers for them
// Execute AI team player conversations with proper cooldown tracking
const executeAiTeamConversations = (team: any, newState: GameState): void => {
    const WEEKS_PER_SEASON = newState.config.WEEKS_PER_SEASON;
    const now = { season: newState.season, week: newState.currentWeek };

    // Generate conversation decisions using OfflineAiService logic (handles cooldown checks)
    const aiActions = OfflineAiService.generateAiPlayerActions(
        team,
        [], // youthSquad not needed for conversation decisions
        [], // transferMarket not needed
        team.finances.balance,
        15, // maxSquadSize
        false, // isManagementWeek not critical for conversations
        now // Pass current game time for cooldown checks
    );

    // Track AI team weekly conversation count (limit 2 per week)
    const conversationTracking = newState.aiTeamConversationsThisWeek || {};
    const teamKey = `${team.name}_${now.season}_${now.week}`;
    const currentTeamCount = conversationTracking[teamKey] || 0;

    // Execute conversations (max 2 per week per team)
    aiActions.playerConversations.forEach((convo, index) => {
        if (currentTeamCount + index >= 2) {
            return; // Skip if we've reached the limit
        }

        const player = team.squad.find(p => p.id === convo.playerId);
        if (!player) return;

        // Check per-player cooldown again (defensive check)
        const playerCooldown = player.nextConversationAvailable;
        const isOnCooldown = playerCooldown && (now.season < playerCooldown.season || (now.season === playerCooldown.season && now.week < playerCooldown.week));

        if (isOnCooldown) {
            return; // Skip if player is on cooldown
        }

        // Apply the conversation effects
        let moraleChange = 0;
        let moraleReason = '';

        const moraleMap: Record<Morale, number> = { 'Ecstatic': 5, 'Happy': 4, 'Content': 3, 'Unhappy': 2, 'Furious': 1 };
        const numberToMorale: Record<number, Morale> = { 5: 'Ecstatic', 4: 'Happy', 3: 'Content', 2: 'Unhappy', 1: 'Furious' };
        const changeMoraleLocal = (current: Morale, change: number) => {
            const newLevel = Math.max(1, Math.min(5, moraleMap[current] + change));
            return numberToMorale[newLevel];
        };

        // Apply morale changes based on choice and personality
        switch (convo.choice) {
            case 'praise':
                moraleChange = (player.personality === 'Professional' || player.personality === 'Loyal' ? 2 : 1);
                moraleReason = 'AI manager praised performance';
                break;
            case 'promise':
                moraleChange = (player.personality === 'Ambitious' ? 2 : 1);
                moraleReason = 'AI manager promised more playing time';
                break;
            case 'show_faith':
                moraleChange = (player.personality === 'Loyal' || player.personality === 'Professional') ? 2 : 1;
                moraleReason = 'AI manager showed faith in ability';
                break;
            case 'demand_improvement':
                moraleChange = (player.personality === 'Professional' || player.personality === 'Ambitious') ? 1 : -1;
                moraleReason = 'AI manager demanded improvement';
                break;
        }

        // Apply the morale change
        player.morale = changeMoraleLocal(player.morale, moraleChange);
        player.moraleReason = moraleReason;

        // Set per-player 4-week cooldown
        let cooldownWeek = now.week + 4;
        let cooldownSeason = now.season;
        if (cooldownWeek > WEEKS_PER_SEASON) {
            cooldownWeek -= WEEKS_PER_SEASON;
            cooldownSeason += 1;
        }
        player.nextConversationAvailable = { season: cooldownSeason, week: cooldownWeek };
    });

    // Update the conversation tracking for this team this week
    if (!newState.aiTeamConversationsThisWeek) {
        newState.aiTeamConversationsThisWeek = {};
    }
    const finalCount = Math.min(2, aiActions.playerConversations.length);
    newState.aiTeamConversationsThisWeek[teamKey] = currentTeamCount + finalCount;
};

export const runWeeklySim = async (gameState: GameState, isAiPunditEnabled: boolean): Promise<{ newState: GameState; playerMatchEvents: MatchEvent[] | null, playerFixture: DisplayFixture | null }> => {
    let newState: GameState = JSON.parse(JSON.stringify(gameState));
    (newState as any).gameStarted = true;
    const currentWeek = newState.currentWeek;
    const moraleCounters = { boosts: 0, drops: 0 };
    let playerMatchEvents: MatchEvent[] | null = null;
    let playerFixtureForReturn: DisplayFixture | null = null;

    // Reset player negotiations counter for the new week
    newState.playerNegotiationsThisWeek = {};

    // Generate weekly briefing from Assistant Manager
    const playerTeam = getPlayerTeam(newState);
    const playerLeague = newState.leagues[newState.playerLeagueName];
    if (playerTeam && playerLeague) {
        const briefing = AssistantManagerBriefingService.generateWeeklyBriefing(newState, playerTeam, playerLeague);

        // Store briefing history
        if (!newState.assistantManagerBriefingHistory) {
            newState.assistantManagerBriefingHistory = [];
        }
        newState.assistantManagerBriefingHistory.push(briefing);

        // Create news item with link to assistant page (include season and week)
        addNewsItem(
            newState,
            '📧 Weekly Briefing from Assistant Manager',
            'Check your weekly briefing from the Assistant Manager.',
            'general',
            undefined,
            { type: 'assistant', title: 'Assistant Manager', season: briefing.season, week: briefing.week }
        );
    }

    // Get upcoming fixtures from the new structure
    const upcomingLeagueWeeks = new Set<number>();
    Object.keys(newState.fixtures.leagues || {}).forEach(leagueName => {
        const weeks = newState.fixtures.leagues[leagueName];
        Object.keys(weeks).forEach(weekKey => {
            const week = parseInt(weekKey, 10);
            if (week >= currentWeek) {
                const weekFixtures = weeks[weekKey];
                if (weekFixtures.some(f => !f.result)) {
                    upcomingLeagueWeeks.add(week);
                }
            }
        });
    });

    const upcomingCupWeeks = new Set<number>();
    Object.keys(newState.cups).forEach(cupName => {
        const weeks = newState.fixtures.cups?.[cupName] || {};
        Object.keys(weeks).forEach(weekKey => {
            const week = parseInt(weekKey, 10);
            if (week >= currentWeek) {
                const weekFixtures = weeks[weekKey];
                if (weekFixtures.some(f => !f.result)) {
                    upcomingCupWeeks.add(week);
                }
            }
        });
    });

    const nextLeagueEvent = Math.min(...upcomingLeagueWeeks, Infinity);
    const nextCupEvent = Math.min(...upcomingCupWeeks, Infinity);
    const nextEventWeek = Math.min(nextLeagueEvent, nextCupEvent);

    if (nextEventWeek === Infinity) {
        const maxWeeks = Math.max(...Object.values(gameState.config.LEAGUE_CONFIG).map(c => (c.teams - 1) * 2));
        newState.currentWeek = maxWeeks + 1;
        return { newState, playerMatchEvents, playerFixture: null };
    }

    const isCupEvent = nextEventWeek === nextCupEvent && nextCupEvent !== Infinity;
    const allPlayersById = new Map<number, Player>();
    const allTeamsByName = new Map<string, any>();
    const allPlayersList: Player[] = [];
    Object.values(newState.leagues).forEach((league: League) => {
        league.teams.forEach(team => {
            allTeamsByName.set(team.name, team);
            team.squad.forEach(player => {
                allPlayersById.set(player.id, player);
                allPlayersList.push(player);
            });
        });
    });
    
    const playersWhoPlayedThisWeek = new Set<number>();
    
    if (isCupEvent) {
        const allLeagueNames = newState.config.LEAGUE_NAMES;

        for (const cupName of Object.keys(newState.cups)) {
            const cup = newState.cups[cupName];
            const cupFixturesForWeek = FixturesHelper.getCupFixturesForWeek(newState.fixtures, cupName, nextEventWeek);
            for (const fixture of cupFixturesForWeek.filter((f: CupFixture) => !f.result)) {
                if (fixture.away === 'BYE') continue;
                const homeTeam = allTeamsByName.get(fixture.home);
                const awayTeam = allTeamsByName.get(fixture.away);
                if (!homeTeam || !awayTeam) continue;
                const simResult = simulateMatch(homeTeam, awayTeam, newState);

                // If cup match is drawn, simulate penalty shootout
                if (simResult.result.homeScore === simResult.result.awayScore) {
                    const homeStartingXI = simResult.result.homeStartingXI.map(id => allPlayersById.get(id)).filter(p => p) as Player[];
                    const awayStartingXI = simResult.result.awayStartingXI.map(id => allPlayersById.get(id)).filter(p => p) as Player[];

                    // Get sent-off players (red cards)
                    const sentOffPlayerIds = new Set(simResult.result.redCards.map(card => card.playerId));

                    const penaltyResult = simulatePenaltyShootout(homeTeam, awayTeam, homeStartingXI, awayStartingXI, sentOffPlayerIds);

                    // Update result with penalties
                    simResult.result.penalties = {
                        homeScore: penaltyResult.homeScore,
                        awayScore: penaltyResult.awayScore,
                    };

                    // Add penalty events to match events
                    simResult.result.events = [...simResult.result.events, ...penaltyResult.events];

                    // Determine winner based on penalties
                    if (penaltyResult.homeScore > penaltyResult.awayScore) {
                        simResult.result.homeScore++;
                    } else {
                        simResult.result.awayScore++;
                    }
                }

                fixture.result = simResult.result;

                const isPlayerTeamInFixture = fixture.home === newState.playerTeamName || fixture.away === newState.playerTeamName;
                const isFinal = fixture.roundName.toLowerCase() === 'final';

                if (isPlayerTeamInFixture) {
                    playerFixtureForReturn = { ...fixture, competition: `${cup.name} ${fixture.roundName}`, type: 'cup' };
                    if (isFinal) {
                        playerMatchEvents = fixture.result ? generateDetailedEvents(fixture.result, fixture.home, fixture.away) : [];
                    } else {
                        // Sort events by minute for consistency
                        playerMatchEvents = fixture.result ? [...fixture.result.events].sort((a, b) => a.minute - b.minute) : [];
                    }
                } else if (isFinal && !playerFixtureForReturn) {
                    // Show cup finals to player even if they're not in it
                    playerFixtureForReturn = { ...fixture, competition: `${cup.name} ${fixture.roundName}`, type: 'cup' };
                    playerMatchEvents = fixture.result ? generateDetailedEvents(fixture.result, fixture.home, fixture.away) : [];
                }

                // Process morale changes for both teams
                MoraleProcessor.processMoraleChanges(homeTeam, simResult.result.homeScore > simResult.result.awayScore, simResult.result.homeScore === simResult.result.awayScore, simResult.result.homeStartingXI, allPlayersById, simResult.result.homePlayerRatings, newState, moraleCounters);
                MoraleProcessor.processMoraleChanges(awayTeam, simResult.result.awayScore > simResult.result.homeScore, simResult.result.homeScore === simResult.result.awayScore, simResult.result.awayStartingXI, allPlayersById, simResult.result.awayPlayerRatings, newState, moraleCounters);

                // Process bench player morale
                processBenchPlayerMorale(homeTeam, simResult.result.homeStartingXI, newState);
                processBenchPlayerMorale(awayTeam, simResult.result.awayStartingXI, newState);
                const winner = simResult.result.homeScore > simResult.result.awayScore ? homeTeam : awayTeam;
                const loser = simResult.result.homeScore > simResult.result.awayScore ? awayTeam : homeTeam;
                const prize = cup.prizeMoney[cup.currentRound] || 0;
                const tvMoney = (newState.config.CUP_CONFIG as any)[cup.name].tvMoneyPerGame;
                const totalWinnings = prize + tvMoney;
                winner.finances.balance += totalWinnings;
                winner.finances.history.push({ season: newState.season, week: Math.floor(newState.currentWeek), event: `${cup.name} Win`, amount: totalWinnings });
                const winnerLeague = Object.entries(newState.leagues).find(([_, l]) => l.teams.some(t => t.name === winner.name))?.[0] || '';
                const loserLeague = Object.entries(newState.leagues).find(([_, l]) => l.teams.some(t => t.name === loser.name))?.[0] || '';
                const winnerLeagueRank = allLeagueNames.indexOf(winnerLeague);
                const loserLeagueRank = allLeagueNames.indexOf(loserLeague);
                if (winnerLeagueRank !== -1 && loserLeagueRank !== -1 && winnerLeagueRank > loserLeagueRank + 1) {
                    const headline = await AiNewsService.generateCupsetHeadline(winner.name, loser.name, winnerLeague, loserLeague, isAiPunditEnabled);
                    addNewsItem(newState, headline, `${winner.name} of ${winnerLeague} have caused a major upset, knocking ${loser.name} from the ${loserLeague} out of the ${cup.name}!`, 'upset', undefined, undefined, { ...fixture, competition: cup.name, type: 'cup' });
                }
                // Track players who played and their ratings
                simResult.result.homeStartingXI.forEach(id => {
                    playersWhoPlayedThisWeek.add(id);
                    const p = allPlayersById.get(id);
                    if (p && simResult.result.homePlayerRatings[id]) {
                        FormProcessor.trackPlayerRatings(p, simResult.result.homePlayerRatings[id]);
                    }
                });
                simResult.result.awayStartingXI.forEach(id => {
                    playersWhoPlayedThisWeek.add(id);
                    const p = allPlayersById.get(id);
                    if (p && simResult.result.awayPlayerRatings[id]) {
                        FormProcessor.trackPlayerRatings(p, simResult.result.awayPlayerRatings[id]);
                    }
                });
                processMatchStats(simResult.result, allPlayersById);
                if (simResult.homeGkId) { const p = allPlayersById.get(simResult.homeGkId); if (p) p.goalsConceded = (p.goalsConceded || 0) + simResult.result.awayScore; }
                if (simResult.awayGkId) { const p = allPlayersById.get(simResult.awayGkId); if (p) p.goalsConceded = (p.goalsConceded || 0) + simResult.result.homeScore; }
                updateClubHistoryStats(homeTeam, new Set(simResult.result.homeStartingXI), simResult.result.homeScorers);
                updateClubHistoryStats(awayTeam, new Set(simResult.result.awayStartingXI), simResult.result.awayScorers);

                // Process suspensions - reduce match counters for both teams
                processSuspensions(homeTeam);
                processSuspensions(awayTeam);
            }
        }
        Object.keys(newState.cups).forEach(cupName => {
            const cup = newState.cups[cupName];
            const roundInfo = cup.rounds[cup.currentRound];
            if (roundInfo) {
                // Get all fixtures for current round from the new structure
                const allCupFixtures = FixturesHelper.getAllCupFixtures(newState.fixtures, cupName);
                const currentRoundFixtures = allCupFixtures.filter(f => f.roundName === roundInfo.name);
                const allFixturesComplete = currentRoundFixtures.length > 0 &&
                    currentRoundFixtures.every(f => f.away === 'BYE' || f.result);

                if (allFixturesComplete) {
                    let advancedCup = CupService.advanceToNextRound(cup, currentRoundFixtures);
                    newState.cups[cupName] = advancedCup;

                    // Generate next round fixtures if cup continues
                    if (advancedCup.currentRound < advancedCup.rounds.length) {
                        const nextRoundFixtures = CupService.generateRoundFixtures(advancedCup, allCupFixtures);
                        const nextRoundWeek = advancedCup.rounds[advancedCup.currentRound].week;
                        // Add next round fixtures to GameState.fixtures.cups (addCupFixtures handles adding to weeks automatically)
                        newState.fixtures = FixturesHelper.addCupFixtures(newState.fixtures, cupName, nextRoundWeek, nextRoundFixtures);
                    }
                }
            }
        });
    } else {
        for (const leagueName in newState.fixtures.leagues) {
            const leagueFixturesForWeek = FixturesHelper.getLeagueFixturesForWeek(newState.fixtures, leagueName, nextEventWeek);
            for (const fixture of leagueFixturesForWeek) {
                if (fixture.result) continue;
                const homeTeam = allTeamsByName.get(fixture.home);
                const awayTeam = allTeamsByName.get(fixture.away);
                if (!homeTeam || !awayTeam) continue;
                const simResult = simulateMatch(homeTeam, awayTeam, newState);
                fixture.result = simResult.result;

                if (fixture.home === newState.playerTeamName || fixture.away === newState.playerTeamName) {
                    // Sort events by minute for consistency
                    playerMatchEvents = [...fixture.result.events].sort((a, b) => a.minute - b.minute);
                    playerFixtureForReturn = { ...fixture, competition: leagueName, type: 'league' };
                }

                updateLeagueTable(homeTeam, simResult.result.homeScore, simResult.result.awayScore);
                updateLeagueTable(awayTeam, simResult.result.awayScore, simResult.result.homeScore);
                // Process morale changes for both teams
                MoraleProcessor.processMoraleChanges(homeTeam, simResult.result.homeScore > simResult.result.awayScore, simResult.result.homeScore === simResult.result.awayScore, simResult.result.homeStartingXI, allPlayersById, simResult.result.homePlayerRatings, newState, moraleCounters);
                MoraleProcessor.processMoraleChanges(awayTeam, simResult.result.awayScore > simResult.result.homeScore, simResult.result.homeScore === simResult.result.awayScore, simResult.result.awayStartingXI, allPlayersById, simResult.result.awayPlayerRatings, newState, moraleCounters);

                // Process bench player morale
                processBenchPlayerMorale(homeTeam, simResult.result.homeStartingXI, newState);
                processBenchPlayerMorale(awayTeam, simResult.result.awayStartingXI, newState);

                // Track players who played and their ratings
                simResult.result.homeStartingXI.forEach(id => {
                    playersWhoPlayedThisWeek.add(id);
                    const p = allPlayersById.get(id);
                    if (p && simResult.result.homePlayerRatings[id]) {
                        FormProcessor.trackPlayerRatings(p, simResult.result.homePlayerRatings[id]);
                    }
                });
                simResult.result.awayStartingXI.forEach(id => {
                    playersWhoPlayedThisWeek.add(id);
                    const p = allPlayersById.get(id);
                    if (p && simResult.result.awayPlayerRatings[id]) {
                        FormProcessor.trackPlayerRatings(p, simResult.result.awayPlayerRatings[id]);
                    }
                });
                processMatchStats(simResult.result, allPlayersById);
                if (simResult.homeGkId) { const p = allPlayersById.get(simResult.homeGkId); if (p) p.goalsConceded = (p.goalsConceded || 0) + simResult.result.awayScore; }
                if (simResult.awayGkId) { const p = allPlayersById.get(simResult.awayGkId); if (p) p.goalsConceded = (p.goalsConceded || 0) + simResult.result.homeScore; }
                updateClubHistoryStats(homeTeam, new Set(simResult.result.homeStartingXI), simResult.result.homeScorers);
                updateClubHistoryStats(awayTeam, new Set(simResult.result.awayStartingXI), simResult.result.awayScorers);

                // Process suspensions - reduce match counters for both teams
                processSuspensions(homeTeam);
                processSuspensions(awayTeam);
            }
        }
    }

    // Injury Processing
    InjuryProcessor.processInjuries(newState, playersWhoPlayedThisWeek, allPlayersById);

    // Form Processing and Injury Recovery
    FormProcessor.processFormChanges(allPlayersList, playersWhoPlayedThisWeek);
    InjuryProcessor.processRecovery(newState, allPlayersList);

    Object.values(newState.leagues).forEach(league => {
        league.teams.forEach(team => {
            const facilityInfo = TRAINING_FACILITIES_LEVELS[team.trainingFacilities.level - 1];
            const trainingStaffWages = facilityInfo ? facilityInfo.weeklyWages : 0;
            
            const academyInfo = YOUTH_ACADEMY_LEVELS[team.youthAcademy.level - 1];
            const academyStaffWages = academyInfo ? academyInfo.weeklyWages : 0;

            const playerWages = team.squad.reduce((sum, player) => sum + player.wage, 0);
            const assistantManagerWages = ASSISTANT_MANAGER_LEVELS[team.assistantManager.level - 1]?.weeklyWages || 0;
            const totalWages = playerWages + trainingStaffWages + academyStaffWages + assistantManagerWages;

            team.finances.balance -= totalWages;
            team.finances.history.push({ season: newState.season, week: newState.currentWeek, event: 'Player Wages', amount: -playerWages });

            if (trainingStaffWages > 0) {
                team.finances.history.push({ season: newState.season, week: newState.currentWeek, event: 'Training Staff Wages', amount: -trainingStaffWages });
            }
            if (academyStaffWages > 0) {
                team.finances.history.push({ season: newState.season, week: newState.currentWeek, event: 'Youth Staff Wages', amount: -academyStaffWages });
            }
            if (assistantManagerWages > 0) {
                const managerLevel = ASSISTANT_MANAGER_LEVELS[team.assistantManager.level - 1];
                team.finances.history.push({ season: newState.season, week: newState.currentWeek, event: `Assistant Manager (${managerLevel.name}) Wages`, amount: -assistantManagerWages });
            }
            
            const leagueFixturesForCurrentWeek = FixturesHelper.getLeagueFixturesForWeek(newState.fixtures, league.name, currentWeek);
            if(leagueFixturesForCurrentWeek.some(f => f.home === team.name)) {
                const attendance = Math.round(league.config.stadiumCapacity * (0.6 + Math.random() * 0.35));
                const gateReceipts = attendance * league.config.ticketPrice;
                team.finances.balance += gateReceipts;
                team.finances.history.push({ season: newState.season, week: newState.currentWeek, event: 'Gate Receipts', amount: gateReceipts });
            }
        });
    });

    if (newState.currentWeek % 4 === 0) {
        Object.values(newState.leagues).forEach(league => {
            league.teams.forEach(team => {
                refreshYouthSquad(team, league.name, newState.config, newState.currentWeek);
                if (team.name === newState.playerTeamName) {
                    addNewsItem(newState, 'Youth Intake', 'A new batch of young players has arrived at the academy.', 'youth', undefined, { type: 'youth', title: 'Youth Academy' });
                }
            });
        });
    }

    TrainingProcessor.processTrainingAndDevelopment(newState, playersWhoPlayedThisWeek);

    Object.values(newState.leagues).forEach(league => {
        sortLeague(league);
        if (!isCupEvent) {
            league.teams.forEach((team, index) => {
                if (!team.positionHistory) team.positionHistory = [];
                team.positionHistory.push({
                    season: newState.season,
                    week: Math.floor(nextEventWeek),
                    position: index + 1
                });
            });
        }
    });

    const simWeek = isCupEvent ? Math.floor(nextEventWeek) : nextEventWeek;
    if (simWeek % 4 === 1 || simWeek === 1) { // Market value updates on management weeks and week 1
        Object.values(newState.leagues).forEach(league => {
            league.teams.forEach(team => {
                team.squad.forEach(player => {
                    const newValue = getPlayerValue(player, league.name, newState.gameType, newState.config);
                    player.marketValue = newValue;
                    if (!player.marketValueHistory) player.marketValueHistory = [];
                    player.marketValueHistory.push({
                        season: newState.season,
                        week: simWeek,
                        value: newValue,
                    });
                });
            });
        });
    }

    // Mid-season wage expectations review
    const midSeasonWeek = Math.floor((newState.config.LEAGUE_CONFIG[newState.playerLeagueName].teams - 1));
    if (simWeek === midSeasonWeek) {
        let playersUpdated = 0;
        let playersWantingTalks = 0;
        Object.values(newState.leagues).forEach(league => {
            league.teams.forEach(team => {
                const starterIds = new Set(Object.values(team.tactics.lineup));
                team.squad.forEach(player => {
                    // Update expectations for players with good/bad form or random 20%
                    if (Math.abs(player.form) >= 2 || Math.random() < 0.2) {
                        const oldExpectedWage = player.expectedWage;
                        const isStarter = starterIds.has(player.id);
                        player.expectedWage = calculateExpectedWage(player, isStarter, league.name, newState.gameType, newState.config);

                        // For player's team: check if they feel significantly underpaid
                        if (team.name === newState.playerTeamName) {
                            playersUpdated++;
                            const wageGap = player.expectedWage - player.wage;
                            const gapPercentage = player.wage > 0 ? (wageGap / player.wage) : 0;

                            // Player wants to talk if:
                            // 1. They're a starter and underpaid by 30%+ OR
                            // 2. They're any player underpaid by 50%+
                            if ((isStarter && gapPercentage > 0.3) || gapPercentage > 0.5) {
                                if (!player.wantsToTalk) {
                                    player.wantsToTalk = true;
                                    player.morale = changeMorale(player.morale, -1);
                                    player.moraleReason = 'Concerned about contract';
                                    playersWantingTalks++;
                                    addNewsItem(newState, 'Player Wants to Talk', `${player.name} believes they deserve a better contract. They want to discuss their wage.`, 'morale', undefined, { type: 'team', title: 'My Team', activeTab: 'morale' });
                                }
                            }
                        }
                    }
                });

                // AI teams: automatically adjust wages for significantly underpaid players
                if (team.name !== newState.playerTeamName) {
                    team.squad.forEach(player => {
                        const wageGap = player.expectedWage - player.wage;
                        const gapPercentage = player.wage > 0 ? (wageGap / player.wage) : 0;
                        // AI gives raises to players underpaid by 40%+
                        if (gapPercentage > 0.4 && team.finances.balance > player.expectedWage * 52) {
                            player.wage = Math.round((player.wage + player.expectedWage) / 2); // Meet them halfway
                            player.morale = changeMorale(player.morale, 1);
                            player.moraleReason = 'Pleased with new wage';
                        }
                    });
                }
            });
        });
        if (playersUpdated > 0) {
            let message = `Wage expectations for ${playersUpdated} of your players have been updated based on recent performances.`;
            if (playersWantingTalks > 0) {
                message += ` ${playersWantingTalks} player(s) want to discuss their contract.`;
            }
            addNewsItem(newState, "Market Update", message, 'general');
        }
    }

    // Execute AI team player conversations (morale management - same limits as human player)
    Object.values(newState.leagues).forEach(league => {
        const aiTeams = league.teams.filter(team => team.name !== newState.playerTeamName);
        aiTeams.forEach(team => executeAiTeamConversations(team, newState));
    });

    // Update AI team tactics, lineups, and training (avoid recursive loops by doing all AI teams at once)
    Object.values(newState.leagues).forEach(league => {
        const aiTeams = league.teams.filter(team => team.name !== newState.playerTeamName);

        // Update tactics every 4 weeks (management weeks)
        if (simWeek % 4 === 1 || simWeek === 1) {
            aiTeams.forEach(updateAITactics);
        }

        // Update lineups every week
        aiTeams.forEach(updateAILineup);

        // Update training focus every week
        aiTeams.forEach(updateAITrainingFocus);
    });

    if (simWeek % 4 === 1 || simWeek === 1) { // In-season transfer logic on management weeks and week 1
        let stateWithUpdatedLists = updateAITransferLists(newState);
        const { newState: stateAfterTransfers, transferCount } = simulateAIInSeasonTransfers(stateWithUpdatedLists);
        newState = stateAfterTransfers;

        if (transferCount > 0) {
            const plural = transferCount > 1 ? 's' : '';
            addNewsItem(
                newState,
                'Transfer Activity',
                `${transferCount} mid-season transfer deal${plural} completed across the leagues.`,
                'transfer',
                undefined,
                { type: 'transfers', title: 'Transfers', activeTab: 'history' }
            );
        }

        // Process AI team facility upgrades on management weeks
        newState = AIFacilityManagementService.processAITeamUpgrades(newState);

        // Process AI team bidding on players (periodic during management weeks)
        newState = AIBiddingService.processAIBidding(newState);

        // Process free agent offers on management weeks
        newState = FreeAgentService.processFreeAgentOffers(newState);
    }

    // Process free agent decisions (resolve bids when deadline is reached)
    // This checks all free agents to see if any have reached their decision deadline
    newState = FreeAgentService.resolveFreeAgentDecisions(newState);

    // Process AI team bids on listed players - AI teams actively bid based on squad needs
    newState = AIBiddingService.bidOnListedPlayers(newState);

    // Generate transfer suggestions for player team (every 2 weeks on management weeks)
    if ((simWeek % 4 === 1 || simWeek === 1) && Math.random() < 0.6) {
        const playerTeam = getPlayerTeam(newState);
        const assistantManagerMgmt = useAssistantManagerManagement(newState);
        const transferSuggestions = assistantManagerMgmt.getTransferRecommendations(playerTeam.name);

        // Add news for significant sell suggestions
        const weakPlayerSuggestions = transferSuggestions.filter(s => s.action === 'sell' && s.skill < 50);
        if (weakPlayerSuggestions.length > 0) {
            const plural = weakPlayerSuggestions.length > 1 ? 's' : '';
            const playerNames = weakPlayerSuggestions.slice(0, 2).map(s => s.playerName).join(', ');
            const morePlayers = weakPlayerSuggestions.length > 2 ? ` and ${weakPlayerSuggestions.length - 2} more` : '';
            addNewsItem(
                newState,
                'Transfer Insight',
                `Your assistant manager suggests reviewing the contract${plural} of ${playerNames}${morePlayers}. They may be surplus to requirements.`,
                'transfer',
                undefined,
                { type: 'manager', title: 'Manager', activeTab: 'assistantManager' }
            );
        }
    }

    if (moraleCounters.boosts > 0 || moraleCounters.drops > 0) {
        let message = '';
        if (moraleCounters.boosts > 0 && moraleCounters.drops > 0) {
            message = `Morale has improved for ${moraleCounters.boosts} player(s) and dropped for ${moraleCounters.drops} player(s) this week.`;
        } else if (moraleCounters.boosts > 0) {
            const plural = moraleCounters.boosts > 1 ? 's' : '';
            message = `Morale has improved for ${moraleCounters.boosts} player${plural} this week.`;
        } else { // drops > 0
            const plural = moraleCounters.drops > 1 ? 's' : '';
            message = `Morale has dropped for ${moraleCounters.drops} player${plural} this week.`;
        }
        addNewsItem(
            newState,
            'Morale Changes',
            message,
            'morale',
            undefined,
            { type: 'team', title: 'My Team', activeTab: 'morale' }
        );
    }

    // Add consolidated transfer news message for this week if any transfers occurred
    const transfersThisWeek = newState.transferHistory.filter(t => t.season === newState.season && t.week === simWeek);
    if (transfersThisWeek.length > 0) {
        const plural = transfersThisWeek.length > 1 ? 's' : '';
        const playerNames = transfersThisWeek.slice(0, 3).map(t => t.playerName).join(', ');
        const moreTransfers = transfersThisWeek.length > 3 ? ` and ${transfersThisWeek.length - 3} more` : '';
        const message = transfersThisWeek.length === 1
            ? `${playerNames} has been transferred.`
            : `${transfersThisWeek.length} transfer${plural} completed: ${playerNames}${moreTransfers}.`;

        addNewsItem(
            newState,
            'Transfer Activity',
            message,
            'transfer',
            undefined,
            { type: 'transfers', title: 'Transfers', activeTab: 'history', filterWeek: simWeek, filterSeason: newState.season }
        );
    }

    // Last step: update fitness for all players
    FitnessProcessor.processFitnessUpdates(newState, playersWhoPlayedThisWeek);

    newState.currentWeek = nextEventWeek + 1;
    
    return { newState, playerMatchEvents, playerFixture: playerFixtureForReturn };
};